/**
 * The program roll-up — V109-5-5 (PRD G-7, G-8; design run `v109-track5-views` round 3, S1 / #7 / #8 /
 * #10; glance critic; built-UI fix pass). One result set, read as program readiness from determinations:
 *
 *   "Ready · 82 of 130 controls met"  [Off track]                       (GovRAMP: the tier being assessed;
 *   "↓3 since yesterday · 2 broke · 2 couldn't check · 1 newly passing"  FedRAMP: the class)
 *   [met ▮▮▮▮▮▮▮|not met ▨|couldn't check ▥|not yet tested ▢]
 *   "Ready needs 130 met · 48 to go before the Ready assessment, Feb 2, 2027"
 *   legend: each non-zero count opens its list on this page (`?status=`), whose rows = the count
 *   NEXT [Fix nw-data-exports]  (the page's one filled primary)
 *   Swept today 06:02 · vs yesterday 06:01 · Scope        [Sweep again]
 *   ▸ Baseline & assessor files (counting, checks version, the files for the assessor — each a real anchor)
 *
 * The bar's 100% is the applicable controls (N/A outside it); Couldn't check and Not yet tested are
 * never met; the status chip carries no second count. Targets and dates come from the program's own
 * data. Everything the views show reads ONE set of determinations (`RollupData.rows`), and
 * `reconcileViews` checks it (a dev-mode assert, `data-reconcile` on the section).
 * `SystemSweepSummary` is the system page's form: that system's resources and changes first.
 *
 * Reads (any member): GET /programs/:p/sweep-report; GovRAMP GET /programs/:p/govramp; FedRAMP GET
 * /frameworks/:fw/readiness?class=; GET /sweeps/summary, /resources, /systems (system summary).
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import { getProgramManifest, hasTieredRequirementSource, isSweepProgram, type ProgramCard, type ProgramManifest, type ProgramSweepReport, type SweepChange } from '@aigovops/core';
import { getFrameworkReadiness, listWorkspaceSystems, type Soc2Requirement } from '../../lib/api';
import { getGovrampOverview, type GovrampOverview } from '../../lib/govramp-api';
import { getSweepReport, listSweepResources, sweepKeys } from '../../lib/sweep-api';
import { queryKeys } from '../../lib/query-keys';
import { cycleModel, tierLabel } from '../../lib/govramp-model';
import { fmtProgramDate } from '../../lib/program-shell';
import { programLabel } from '../../lib/boundary-model';
import { isCouldntRequirement } from '../../lib/couldnt-check';
import { usePrograms } from '../../lib/workspace-navigation';
import {
  ASSESSMENT_RESULTS_ANCHOR, bucketOfCheckState, bucketOfGovramp, changeStatement, changesHref, clockText, currentStage, foundText, inventoryHref, metDelta,
  nextByProgram, programHref, reconcileViews, rollupFromCheckCounts, rollupFromGovramp, rollupModel, sinceText, targetText, unitNoun, unitOf, verdictsHref,
  type ControlBucket, type ControlRowLite, type RollupCounts,
} from '../../lib/sweep-model';
import { useAnnounce } from '../today/shared';
import { SweepFreshness } from './SweepFreshness';
import { useSweepSummary } from './SweepNotice';
import './sweep.css';

/** One control as the program shows it (the Readiness list's row). */
export interface ControlRow extends ControlRowLite {
  title: string;
  /** The program's own row (GovRAMP overview / FedRAMP readiness), for the row's action. */
  govramp?: NonNullable<GovrampOverview['readiness']>['requirements'][number];
  fedramp?: Soc2Requirement;
}

/** The GovRAMP ConMon cycle the page's one-liner and the assessor files point at. */
export interface ConmonLine { tier: string; label: string; done: number; total: number; due: string; late: boolean; stage: 'prepare' | 'operate'; todayDay: string }

export interface RollupData {
  counts: RollupCounts;
  /** "Ready" (GovRAMP tier) / "Class B" (FedRAMP class). */
  label: string;
  by: { what: string; date: string } | null;
  report: ProgramSweepReport;
  /** Every control of the tier / class, in its list (rows per list = the counts). */
  rows: ControlRow[];
  conmon: ConmonLine | null;
  /** The GovRAMP period id (uploads on a row); null for FedRAMP. */
  periodId: string | null;
}

/** The roll-up heading the page: the panels below hide their own readiness hero, primary and lists. */
export interface RollupContextValue { data: RollupData; m: ProgramManifest; card: ProgramCard }
export const RollupContext = createContext<RollupContextValue | null>(null);
export const useRollupShown = () => useContext(RollupContext) !== null;
export const useRollupContext = () => useContext(RollupContext);

/** A FedRAMP requirement's list (older Workers without `check_state`: read from the crediting controls). */
function fedrampBucket(r: Soc2Requirement): ControlBucket {
  if (r.check_state) return bucketOfCheckState(r.check_state);
  if (r.status === 'met') return 'met';
  if (isCouldntRequirement(r)) return 'couldnt_check';
  return r.crediting_controls.some((c) => c.state === 'fail') ? 'not_met' : 'not_tested';
}

type FrameworkReadinessData = Awaited<ReturnType<typeof getFrameworkReadiness>>;

/** Pure: the roll-up from a program's sweep report and its own readiness (GovRAMP overview / FedRAMP readiness). */
export function buildRollup(m: ProgramManifest, card: ProgramCard, report: ProgramSweepReport, overview: GovrampOverview | undefined, fr: FrameworkReadinessData | undefined): RollupData | null {
  if (!report.run) return null;
  const today = new Date().toISOString().slice(0, 10);
  // v10.6 (ADR-0079): only a tiered baseline source (GovRAMP) reads the tier overview; FedRAMP / SOC 2
  // carry framework or keyed sources now and read framework readiness.
  if (hasTieredRequirementSource(m)) {
    const o = overview;
    if (!o?.readiness) return null;
    const by = o.ready_booking ? { what: `the ${tierLabel(o.tier)} assessment`, date: fmtProgramDate(o.ready_booking.assessment_date) }
      : o.period ? { what: 'the period ends', date: fmtProgramDate(o.period.period_end) } : null;
    const rows: ControlRow[] = o.readiness.requirements.map((r) => ({ requirement_id: r.requirement_id, ref: r.ref, title: r.title, bucket: bucketOfGovramp(r), govramp: r }));
    const todayDay = o.generated_at.slice(0, 10);
    const c0 = o.cycles[0];
    const cm = c0 ? cycleModel(c0, todayDay) : null;
    const cyclePeriod = c0 ? card.periods.find((p) => p.period_id === c0.period_id) : undefined;
    const conmon: ConmonLine | null = c0 && cm ? {
      tier: tierLabel(c0.tier ?? o.tier), label: cm.label, done: cm.done, total: cm.total, due: cm.due, late: cm.late, todayDay,
      stage: cyclePeriod?.stage === 'certified' ? 'operate' : currentStage(card),
    } : null;
    return { counts: rollupFromGovramp(o.readiness.counts), label: tierLabel(o.tier) || programLabel(card.program), by, report, rows, conmon, periodId: o.period?.period_id ?? null };
  }
  if (!fr?.check_counts) return null;
  const p = card.periods.find((x) => x.active && x.window);
  const by = p?.window && p.window.start.slice(0, 10) > today ? { what: 'the assessment window opens', date: fmtProgramDate(p.window.start) }
    : card.target_date ? { what: 'your target date', date: fmtProgramDate(card.target_date) } : null;
  const rows: ControlRow[] = fr.requirements.map((q) => ({ requirement_id: q.requirement_id, ref: q.ref, title: q.title, bucket: fedrampBucket(q), fedramp: q }));
  return { counts: rollupFromCheckCounts(fr.check_counts), label: card.certification_class ? `Class ${card.certification_class}` : programLabel(card.program), by, report, rows, conmon: null, periodId: null };
}

/** The three reads behind a roll-up (shared query keys, so Today, the shell and the Readiness view read one cache). */
function rollupQueries(slug: string, m: ProgramManifest | undefined, card: ProgramCard | null, on: boolean, hasRun: boolean) {
  const fw = card?.framework_id ?? m?.frameworkId ?? '';
  const tiered = hasTieredRequirementSource(m);
  return {
    report: { queryKey: sweepKeys.report(slug, m?.key ?? ''), queryFn: () => getSweepReport(slug, m!.key), enabled: on, retry: false, staleTime: 30_000 },
    overview: { queryKey: queryKeys.govrampOverview(slug, card?.profile_id ?? ''), queryFn: () => getGovrampOverview(slug, card!.profile_id), enabled: on && tiered && hasRun, retry: false, staleTime: 30_000 },
    fr: { queryKey: ['framework-readiness', slug, fw, card?.certification_class ?? null], queryFn: () => getFrameworkReadiness(slug, fw, card!.certification_class), enabled: on && !tiered && hasRun, retry: false },
  } as const;
}

/** The roll-up's numbers for one program, or null while loading / without a sweep. */
export function useRollupData(slug: string, m: ProgramManifest | undefined, card: ProgramCard | null, enabled = true): { data: RollupData | null; loading: boolean } {
  const on = enabled && !!m && !!card && isSweepProgram(m);
  const report = useQuery(rollupQueries(slug, m, card, on, false).report);
  const q = rollupQueries(slug, m, card, on, !!report.data?.run);
  const overview = useQuery(q.overview);
  const fr = useQuery(q.fr);
  const tiered = hasTieredRequirementSource(m);
  return useMemo(() => {
    if (!on || !report.data?.run || !card || !m) return { data: null, loading: on && report.isLoading };
    const data = buildRollup(m, card, report.data, overview.data, fr.data);
    return { data, loading: !data && (tiered ? overview.isLoading : fr.isLoading) };
  }, [on, m, report.data, report.isLoading, card, tiered, overview.data, overview.isLoading, fr.data, fr.isLoading]);
}

/**
 * BU-3: the roll-up for every sweep program card at once (Today). program → data; a program without a
 * sweep (or still loading) is absent, and its card keeps its own status.
 */
export function useRollupsFor(slug: string, cards: readonly ProgramCard[]): { byProgram: ReadonlyMap<string, RollupData>; loading: boolean } {
  const list = cards.flatMap((card) => { const m = getProgramManifest(card.program); return m && isSweepProgram(m) ? [{ m, card }] : []; });
  const reports = useQueries({ queries: list.map(({ m, card }) => rollupQueries(slug, m, card, true, false).report) });
  const runs = reports.map((r) => !!r.data?.run);
  const details = useQueries({ queries: list.map(({ m, card }, i) => { const q = rollupQueries(slug, m, card, true, runs[i]!); return hasTieredRequirementSource(m) ? q.overview : q.fr; }) });
  const key = [...reports, ...details].map((q) => q.dataUpdatedAt).join('|');
  return useMemo(() => {
    const byProgram = new Map<string, RollupData>();
    list.forEach(({ m, card }, i) => {
      const rep = reports[i]!.data;
      if (!rep?.run) return;
      const d = details[i]!.data;
      const tiered = hasTieredRequirementSource(m);
      const data = buildRollup(m, card, rep, tiered ? d as GovrampOverview : undefined, tiered ? undefined : d as FrameworkReadinessData);
      if (data) byProgram.set(card.program, data);
    });
    return { byProgram, loading: reports.some((r) => r.isLoading) || details.some((d, i) => runs[i] && d.isLoading) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, cards]);
}

/** BU-2/BU-3: the words every surface uses for a program's state once a sweep exists ("Core · 26 of 44 controls met", Off track). */
export function rollupHeadline(m: ProgramManifest, data: RollupData): { text: string; met: number; applicable: number; toGo: number; noun: string; status: { tone: 'on_track' | 'at_risk' | 'off_track'; text: string } } {
  const unit = unitOf(m);
  const model = rollupModel(data.counts, data.report.drift.newly_failing, unit);
  const noun = unitNoun(unit, model.applicable);
  return { text: `${data.label} · ${data.counts.met} of ${model.applicable} ${noun} met`, met: data.counts.met, applicable: model.applicable, toGo: model.toGo, noun, status: model.status };
}

/**
 * BU-2: the roll-up on a program's other views (Changes, Scope, Audit …) — one compact line that links to
 * the Readiness view, so no older verdict contradicts it. The same for every sweep program.
 */
export function RollupChip({ prefix, m, card, data }: { prefix: string; m: ProgramManifest; card: ProgramCard; data: RollupData }) {
  const h = rollupHeadline(m, data);
  return (
    <p className="sw-rollchip" data-part="rollup-chip">
      <Link to={programHref(prefix, m.key, currentStage(card))}>
        <span>{h.text}</span>{' '}
        <span className="sw-status" data-tone={h.status.tone}><span aria-hidden="true">{h.status.tone === 'off_track' ? '✕ ' : h.status.tone === 'at_risk' ? '! ' : '✓ '}</span>{h.status.text}</span>{' '}
        <span className="sw-rollchip-go">Readiness <span aria-hidden="true">→</span></span>
      </Link>
    </p>
  );
}

/** The anchor of the program's control list on the Readiness view. */
export const CONTROLS_ANCHOR = 'controls-h';

/** Where a roll-up count opens the program's own list on its current stage (the Readiness view's `?status=`). */
export function rollupListHref(prefix: string, m: ProgramManifest, card: Pick<ProgramCard, 'program' | 'periods'>, bucket: string, extra: Record<string, string | null> = {}): string | null {
  return programHref(prefix, m.key, currentStage(card), { status: bucket, ...extra }, CONTROLS_ANCHOR);
}

/** Dev-mode assert (the prototype's `check()`): logs what does not reconcile; the section carries `data-reconcile`. */
export function useReconcile(where: string, input: Parameters<typeof reconcileViews>[0] | null): string[] {
  const errs = useMemo(() => (input ? reconcileViews(input) : []), [input]);
  if (errs.length && import.meta.env.DEV) console.error('reconcile', where, errs);
  return errs;
}

const SEG_TEXT = { met: 'met', not_met: 'not met', couldnt_check: "couldn't check", not_tested: 'not yet tested' } as const;

function Bar({ c, m, compact }: { c: RollupCounts; m: ReturnType<typeof rollupModel>; compact?: boolean }) {
  const aria = `${c.met} met, ${c.not_met} not met, ${c.couldnt_check} couldn't be checked, ${c.not_tested} not yet tested, of ${m.applicable}`;
  // Built-UI Nit: no target tick — the target is every applicable control, the bar's own end.
  return (
    <div className="sw-bar" role="img" aria-label={aria} data-part="rollup-bar">
      {m.segments.filter((s) => s.n).map((s) => (
        <span key={s.key} className="sw-seg" data-k={s.key} style={{ flexGrow: s.n }}>{!compact && s.key === 'not_tested' ? `${s.n} not yet tested` : null}</span>
      ))}
    </div>
  );
}

/** "↓3 since yesterday · 2 broke …": one link; the name starts with the visible words (label in name). */
export function StatementParts({ parts }: { parts: ReturnType<typeof changeStatement> }) {
  return <>{parts.map((p, i) => (
    <span key={p.key} data-tone={p.tone}>
      {i ? <span aria-hidden="true" className="sw-sep">{' · '}</span> : null}
      {p.key === 'delta' ? <><span className="sr-only">{p.tone === 'bad' ? 'down ' : 'up '}</span><span aria-hidden="true">{p.text.slice(0, 1)}</span>{p.text.slice(1)}</> : p.text}
      {i < parts.length - 1 ? <span className="sr-only">,</span> : null}
    </span>
  ))}</>;
}

/** The full roll-up, the program page header on its current stage. */
export function ProgramRollup({ slug, prefix, m, card, data, canSweep, listHref }: {
  slug: string; prefix: string; m: ProgramManifest; card: ProgramCard; data: RollupData; canSweep: boolean;
  /** Where each legend count opens the program's control list (the Readiness view); null = no list for it. */
  listHref: (bucket: string) => string | null;
}) {
  const [live, announce] = useAnnounce();
  const r = data.report;
  const c = data.counts;
  const unit = unitOf(m);
  const model = rollupModel(c, r.drift.newly_failing, unit);
  const since = sinceText(r.compared_with);
  const parts = changeStatement({ delta: metDelta(r), broke: r.drift.newly_failing, couldnt: r.drift.unevaluated, passing: r.drift.newly_passing, since, unit });
  const toChanges = changesHref(prefix, card);
  const top = r.changes.find((x) => x.category === 'newly_failing') ?? null;
  const tiered = hasTieredRequirementSource(m);
  const errs = useReconcile(`roll-up ${m.key}`, useMemo(() => ({ counts: c, rows: data.rows, report: r }), [c, data.rows, r]));
  const partial = r.run?.state === 'partial';
  const next: ReactNode = top
    ? <Link className="gp-linkbtn btn-primary" data-part="rollup-next" to={changesHref(prefix, card, { open: `fix-${top.id}` })}>{`Fix ${top.resource?.name ?? 'the first'}`}</Link>
    : r.drift.unevaluated ? <Link className="gp-linkbtn btn-primary" data-part="rollup-next" to={toChanges}>{`Fix what GAC Pro couldn't check · ${r.drift.unevaluated}`}</Link>
      : c.not_met && listHref('not_met') ? <Link className="gp-linkbtn btn-primary" data-part="rollup-next" to={listHref('not_met')!}>{`Fix ${c.not_met} not met`}</Link> : null;
  const nextWhy = top ? `${top.affects.find((a) => a.program === m.key)?.ref ?? ''} ${top.title ?? ''} · needs your engineering team`.trim() : null;
  // Built-UI #4: each assessor file opens its own section; a file whose section is not on this branch is not listed.
  const results = ASSESSMENT_RESULTS_ANCHOR && m.packages.kinds.includes('oscal_ar_program') ? programHref(prefix, m.key, 'report', {}, ASSESSMENT_RESULTS_ANCHOR) : null;
  const files: Array<[string, string]> = [
    ...(tiered && data.conmon ? [[`ConMon bundle · ${data.conmon.label}`, programHref(prefix, m.key, data.conmon.stage, {}, 'gr-conmon-h')] as [string, string]] : []),
    ...(!tiered && results ? [['KSI validation package', results] as [string, string]] : []),
    ...(results ? [['Assessment results', results] as [string, string]] : []),
  ];

  return (
    <section className="sw-rollup" aria-labelledby="ru-h" data-part="rollup" data-reconcile={errs.length ? errs.join('; ') : 'ok'}>
      {live}
      <h2 className="sw-hero" id="ru-h">
        <span className="sw-hero-label">{`${data.label} ·`}</span>
        <span className="sw-hero-num">{c.met}</span>
        <span className="sw-hero-of">{`of ${model.applicable} ${unitNoun(unit, model.applicable)} met`}</span>
        <span className="sw-status" data-tone={model.status.tone} data-part="rollup-status"><span aria-hidden="true">{model.status.tone === 'off_track' ? '✕ ' : model.status.tone === 'at_risk' ? '! ' : '✓ '}</span>{model.status.text}</span>
      </h2>
      {parts.length ? (
        <p className="sw-statement" data-part="rollup-statement">
          <Link to={toChanges}><StatementParts parts={parts} /></Link>
          {partial ? <span className="sw-dim">{' · from a partial sweep'}</span> : null}
        </p>
      ) : <p className="sw-statement sw-dim">{`No change ${since}${partial ? ' · from a partial sweep' : ''}`}</p>}
      <Bar c={c} m={model} />
      <p className="sw-target" data-part="rollup-target">{targetText(model, data.label, data.by)}</p>
      <ul className="sw-legend" aria-label="Counts">
        {(['met', 'not_met', 'couldnt_check', 'not_tested'] as const).map((k) => {
          const href = c[k] ? listHref(k) : null;
          return (
            <li key={k}>
              <span className="sw-sw sw-seg" data-k={k} aria-hidden="true" />
              {href ? <Link to={href} data-legend={k}><b>{c[k]}</b> {SEG_TEXT[k]}</Link> : <span data-legend={k}><b>{c[k]}</b> {SEG_TEXT[k]}</span>}
              {k === 'couldnt_check' ? <span className="sw-chip" data-tone="dashed">not counted</span> : null}
            </li>
          );
        })}
        {c.not_applicable ? <li>{listHref('not_applicable') ? <Link to={listHref('not_applicable')!} data-legend="not_applicable">{`+${c.not_applicable} not applicable`}</Link> : `+${c.not_applicable} not applicable`}</li> : null}
      </ul>
      {next ? <div className="sw-next"><span className="sw-next-k">Next</span>{next}{nextWhy ? <span className="sw-dim">{nextWhy}</span> : null}</div> : null}
      <SweepFreshness slug={slug} prefix={prefix} run={r.run} compared={r.compared_with} sources={r.sources} canSweep={canSweep} announce={announce} id="ru-fresh" />
      <details className="sw-files">
        {/* BU-9: never name files that are not there — "Baseline" until an assessor file has its anchor. */}
        <summary data-part="rollup-files">{files.length ? 'Baseline & assessor files' : 'Baseline'}</summary>
        <ul>
          <li>{tiered ? `Counting: the ${data.label} tier · ${c.total} ${unitNoun(unit, c.total)}` : `Counting: ${data.label} · ${c.total} ${unitNoun(unit, c.total)}`}</li>
          <li>{`Checks · ${r.run?.catalog_version ? `set of ${r.run.catalog_version.split('@').pop()}` : 'current set'} · sweep ${clockText(r.run?.finished_at)}`}</li>
          <li>Source: your determinations and evidence, with the sweep's results</li>
          {files.map(([t, href]) => <li key={t}><Link to={href}>{t}</Link></li>)}
        </ul>
      </details>
    </section>
  );
}

/** The compact card (system summary): hero + chip, the bar, the non-met counts, "What changed · N". */
export function RollupCard({ prefix, m, card, data }: { prefix: string; m: ProgramManifest; card: ProgramCard; data: RollupData }) {
  const c = data.counts;
  const unit = unitOf(m);
  const model = rollupModel(c, data.report.drift.newly_failing, unit);
  const delta = metDelta(data.report);
  const stage = currentStage(card);
  const listed = (k: string, n: number, text: ReactNode) => { const h = n ? rollupListHref(prefix, m, card, k) : null; return h ? <Link to={h}>{text}</Link> : <span>{text}</span>; };
  return (
    <article className="sw-card" aria-labelledby={`rc-${m.key}`} data-part="rollup-card">
      <h3 id={`rc-${m.key}`}><Link to={programHref(prefix, m.key, stage)}>{programLabel(m.key)}</Link></h3>
      <p className="sw-hero">
        <span className="sw-hero-label">{`${data.label} ·`}</span>
        <span className="sw-hero-num">{c.met}</span>
        {/* punch #13: screen readers hear "82 of 130 controls met", then the change (visually the arrow sits by the number). */}
        <span className="sw-hero-of">{`of ${model.applicable} ${unitNoun(unit, model.applicable)} met`}</span>
        {delta ? <span className="sw-delta sw-delta--lead" data-tone={delta < 0 ? 'bad' : 'good'}><span aria-hidden="true">{delta < 0 ? '↓' : '↑'}{Math.abs(delta)}</span><span className="sr-only">{`, ${delta < 0 ? 'down' : 'up'} ${Math.abs(delta)} since the last sweep`}</span></span> : null}
      </p>
      <span><span className="sw-status" data-tone={model.status.tone}><span aria-hidden="true">{model.status.tone === 'off_track' ? '✕ ' : model.status.tone === 'at_risk' ? '! ' : '✓ '}</span>{model.status.text}</span></span>
      <Bar c={c} m={model} compact />
      <p className="sw-target">{`${model.toGo} to go`}</p>
      <ul>
        <li>{listed('not_met', c.not_met, <><b>{c.not_met}</b> not met</>)}</li>
        <li>{listed('couldnt_check', c.couldnt_check, <><b>{c.couldnt_check}</b> couldn't check</>)} <span className="sw-chip" data-tone="dashed">not counted</span></li>
        <li>{listed('not_tested', c.not_tested, <><b>{c.not_tested}</b> not yet tested</>)}</li>
        {c.not_applicable ? <li>{listed('not_applicable', c.not_applicable, `+${c.not_applicable} not applicable`)}</li> : null}
        <li><Link to={changesHref(prefix, card)}>{`What changed · ${data.report.changes.length}`}</Link></li>
      </ul>
    </article>
  );
}

function CardFor({ slug, prefix, card }: { slug: string; prefix: string; card: ProgramCard }) {
  const m = getProgramManifest(card.program);
  const { data } = useRollupData(slug, m, card);
  if (!m || !data) return null;
  return <RollupCard prefix={prefix} m={m} card={card} data={data} />;
}

/** This system's change rows, per program (from each program's report; a change is the system's when its resource is). */
function useSystemChanges(slug: string, programs: readonly string[], resourceIds: ReadonlySet<string>) {
  const reports = useQueries({ queries: programs.map((p) => ({ queryKey: sweepKeys.report(slug, p), queryFn: () => getSweepReport(slug, p), retry: false, staleTime: 30_000 })) });
  const datas = reports.map((r) => r.data);
  const key = datas.map((d) => d?.generated_at ?? '').join('|');
  return useMemo(() => {
    const seen = new Map<string, SweepChange>();
    for (const r of datas) for (const ch of r?.changes ?? []) if (ch.resource && resourceIds.has(ch.resource.resource_id) && !seen.has(ch.id)) seen.set(ch.id, ch);
    return [...seen.values()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, resourceIds]);
}

/**
 * The system page's summary (built-UI #9): "In <system>" — that system's resources and what changed on
 * them, with Next naming the resource and the controls it affects per program. Program readiness is
 * workspace-wide: its cards show only in a single-system workspace, else a link to all systems.
 */
export function SystemSweepSummary({ slug, prefix, canSweep, systemId, systemName }: { slug: string; prefix: string; canSweep: boolean; systemId: string; systemName: string }) {
  const [live, announce] = useAnnounce();
  const s = useSweepSummary(slug).data;
  const cards = (usePrograms(slug).data?.programs ?? []).filter((c) => isSweepProgram(getProgramManifest(c.program)))
    .sort((a, b) => (getProgramManifest(a.program)!.order - getProgramManifest(b.program)!.order));
  const resources = useQuery({ queryKey: sweepKeys.resources(slug), queryFn: () => listSweepResources(slug), enabled: !!s?.run, retry: false });
  const systems = useQuery({ queryKey: queryKeys.systems(slug), queryFn: () => listWorkspaceSystems(slug), enabled: !!s?.run, staleTime: 60_000, retry: false });
  const mine = useMemo(() => (resources.data?.resources ?? []).filter((r) => !r.gone_at && r.system_id === systemId), [resources.data, systemId]);
  const ids = useMemo(() => new Set(mine.map((r) => r.resource_id)), [mine]);
  const changes = useSystemChanges(slug, cards.map((c) => c.program), ids);
  if (!s?.run || !cards.length) return null;
  const single = (systems.data?.systems.length ?? 0) <= 1;
  const cardOf = (program: string) => cards.find((c) => c.program === program) ?? null;
  // BU-13: one Next band per program (its top change, its refs, its changes view); the first is the filled primary.
  const nexts = nextByProgram(cards.map((c) => c.program), changes);
  const lead = cards[0]!.program;
  return (
    <section className="sw-view" aria-labelledby="ss-h" data-part="system-summary">
      {live}
      <h2 className="gp-sec-h" id="ss-h">{`In ${systemName}`}</h2>
      <SweepFreshness slug={slug} prefix={prefix} run={s.run} compared={s.compared_with} sources={s.sources} canSweep={canSweep} announce={announce} id="ss-fresh" />
      {nexts.map(({ program, change: top, refs }, i) => {
        const pc = cardOf(program)!;
        const broke = top.category === 'newly_failing';
        return (
          <div key={program} className="sw-band" data-tone={broke ? 'bad' : 'warn'} data-part="system-next" data-program={program}>
            <span className="sw-band-main">
              <b>{`Next: ${broke ? 'fix' : 'check'} ${top.resource?.name ?? 'the resource'}`}</b>
              <span className="sw-affects-line">
                <span><Link to={programHref(prefix, program, currentStage(pc))}>{programLabel(program)}</Link>{` ${refs.join(', ')}`}</span>
              </span>
            </span>
            <Link className={`gp-linkbtn${i === 0 ? ' btn-primary' : ' gp-linkbtn--compact'}`} to={broke ? changesHref(prefix, pc, { open: `fix-${top.id}` }) : changesHref(prefix, pc)}>
              {broke ? `Fix ${top.resource?.name ?? 'it'}` : 'Open what changed'}<span className="sr-only">{` · ${programLabel(program)}`}</span></Link>
          </div>
        );
      })}
      <div className="sw-band" data-part="system-found">
        <span className="sw-band-main"><b>{`${mine.length} resource${mine.length === 1 ? '' : 's'}`}</b>
          <span className="sw-dim">{changes.length ? `${changes.length} changed ${sinceText(s.compared_with)}` : `Nothing changed ${sinceText(s.compared_with)}`}</span></span>
        <Link className="gp-linkbtn gp-linkbtn--compact" to={inventoryHref(prefix, lead)}>Open the inventory</Link>
      </div>
      {changes.length ? (
        <ul className="sw-sys-changes" aria-label={`What changed in ${systemName}`}>
          {changes.map((c) => {
            const pc = cardOf(c.affects[0]?.program ?? lead);
            return <li key={c.id}><span className="sw-chip" data-tone={c.category === 'newly_failing' ? 'bad' : c.category === 'unevaluated' ? 'warn' : undefined}>
              {c.category === 'newly_failing' ? 'Broke' : c.category === 'unevaluated' ? "Couldn't check" : c.category === 'newly_passing' ? 'Newly passing' : 'Scope change'}</span>
              {pc ? <Link to={changesHref(prefix, pc, c.category === 'newly_failing' ? { open: `fix-${c.id}` } : {})}>{`${c.resource?.name ?? ''} · ${c.title ?? ''}`}</Link> : <span>{`${c.resource?.name ?? ''} · ${c.title ?? ''}`}</span>}</li>;
          })}
        </ul>
      ) : null}
      {single ? <>
        <h2 className="gp-sec-h">Programs</h2>
        <div className="sw-cards">{cards.map((c) => <CardFor key={c.profile_id} slug={slug} prefix={prefix} card={c} />)}</div>
        <div className="sw-band"><span className="sw-band-main"><b>{foundText(s.found, s.clues_open)}</b></span>
          {s.verdicts_need_you ? <Link className="gp-linkbtn gp-linkbtn--compact" to={verdictsHref(prefix, lead)}>{`${s.verdicts_need_you} need${s.verdicts_need_you === 1 ? 's' : ''} a verdict`}</Link> : null}</div>
      </> : (
        <p className="sw-dim sw-note sw-all-systems" data-part="all-systems">
          {'Program readiness covers every system · '}<Link to={`${prefix}/systems`}>All systems</Link>
          {cards.map((c) => <span key={c.program}><span aria-hidden="true">{' · '}</span><Link to={programHref(prefix, c.program, currentStage(c))}>{programLabel(c.program)}</Link></span>)}
        </p>
      )}
    </section>
  );
}
